<template>
  <div v-if="isLoaded" class="accordion" role="tablist">
    <template v-for="(businessRole, businessRoleIndex) in organizationBusinessRoles">
      <b-card no-body class="mb-1" :key="businessRoleIndex">
        <b-card-header header-tag="header" class="p-3 bg-secondary text-white" role="tab" v-b-toggle="businessRole.name + '-section'" variant="info" @click="sectionChange(businessRoleIndex)">
          <i :class="activeRoleSection == businessRoleIndex ? 'fas fa-caret-up' : 'fas fa-caret-down'"></i> {{ businessRole.name }}
        </b-card-header>
        <b-collapse :id="businessRole.name + '-section'" accordion="my-accordion" role="tabpanel">
          <b-card-body>
            <div class="mb-2 text-right" v-if="canEdit">
                <button @click="addingOrganizationRoleId=businessRole.id;addingOrganizationRoleName=businessRole.name;isModalVisible=true;" 
                        class="btn btn-primary d-print-none" 
                        data-toggle="modal">
                        <i class="fa fa-plus"></i>Add
                </button>
            </div>
            <BTable :busy="!isLoaded"
                    :fields="tableFields"
                    :items="personOrganizationRoles.filter(role => { return role.organizationRoleId == businessRole.id })"
                    :responsive="true"
                    primary-key="id"
                    show-empty
                    striped
                    table-class="table-align-middle">

                <template v-slot:cell(id)="row">
                    <b-button-group v-if="canEdit">
                      <div v-if="row.index === 0"
                         style="width:32px;">
                      </div>
                      <div v-if="row.index === personOrganizationRoles.filter(role => { return role.organizationRoleId == businessRole.id }).length - 1"
                         style="width:32px;">
                      </div>
                      <b-button v-if="row.index !== 0"
                                size="sm"
                                variant="primary"
                                :disabled="isSubmitting"
                                @click="onMoveUpClick(row.item)"
                                style="width:32px;">
                          <i class="fas fa-arrow-up"></i>
                      </b-button>
                      <b-button v-if="row.index !== personOrganizationRoles.filter(role => { return role.organizationRoleId == businessRole.id }).length - 1"
                                :disabled="isSubmitting"
                                @click="onMoveDownClick(row.item)"
                                size="sm"
                                variant="primary"
                                style="width:32px;">
                          <i class="fas fa-arrow-down"></i>
                      </b-button>
                      <b-button :disabled="isSubmitting" 
                                @click="onDeleteClick(row.item)" 
                                size="sm"
                                variant="danger">
                          <i class="fa fa-trash"></i>Delete
                      </b-button>
                    </b-button-group>
                </template>

                <template v-slot:cell(organizationRoleOrdinal)="row">
                    <span>{{row.item.organizationRoleOrdinal}}</span>
                </template>

                <template v-slot:cell(personName)="row">
                    <span>{{row.item.personName}}</span>
                </template>

                <template v-slot:cell(personEmploymentStatus)="row">
                    <span>{{row.item.personEmploymentStatus}}</span>
                </template>

                <template v-slot:cell(organizationCode)="row">
                    <span>{{row.item.organizationCode}}</span>
                </template>
            </BTable>
          </b-card-body>
        </b-collapse>
      </b-card>   
    </template>
    
    <TheOrganizationBusinessRoleForm :organization-id="organizationId" 
                                     :organization-role-id="addingOrganizationRoleId"
                                     :organization-role-name="addingOrganizationRoleName"
                                     :visible="isModalVisible"
                                     @create="reload()"
                                     @close="isModalVisible=false;addingOrganizationRoleId=null;addingOrganizationRoleName=null;"
                                     v-if="canEdit"/>
    <AppModalConfirm :visible="deletingPersonOrganizationRole" 
                     @confirm="onDeleteConfirmed()"
                     @cancel="deletingPersonOrganizationRole=null;">
        <template v-slot:header>Confirm</template>
        <p v-if="deletingPersonOrganizationRole">Are you sure you want to delete the {{deletingPersonOrganizationRole.personName}} from business role '{{deletingPersonOrganizationRole.organizationRoleName}}'?</p>
    </AppModalConfirm>
  </div>
</template>

<script>
    import OrganizationService from "../../services/OrganizationService";
    import TheOrganizationBusinessRoleForm from "@/components/organizations/TheOrganizationBusinessRoleForm";
    import AppModalConfirm from "@/components/shared/AppModalConfirm";
    import {ToastType} from "@/services/models/shared/ToastType";
    import {ApplicationRole} from "@/services/models/Role";

    export default {
        components: {
            AppModalConfirm,
            TheOrganizationBusinessRoleForm
        },
        data() {
            return {
                isLoaded: false,
                activeRoleSection: null,
                isModalVisible: false,
                addingOrganizationRoleId: null,
                addingOrganizationRoleName: null,
                deletingPersonOrganizationRole: null,
                personOrganizationRoles: [],
                tableFields: [
                    {
                        key: 'id',
                        label: '',
                        thStyle: 'width:200px'
                    },
                    {
                        key: 'organizationRoleOrdinal',
                        label: 'Order',
                        thClass: 'text-center',
                        tdClass: 'text-center'
                    },
                    {
                        key: 'personName',
                        label: 'Person',
                    },
                    {
                        key: 'personEmploymentStatus',
                        label: 'Person Status'
                    },
                    {
                        key: 'organizationCode',
                        label: 'Organization'
                    }
                ]
            }
        },
        props: {
            organizationBusinessRoles: {
                required: true
            },
            organizationId: {
                type: String,
                required: true
            }
        },
        computed: {
            /**
             * @description Indicates if this form can be edited by the person based on the given role
             * @returns {boolean}
             */
            canEdit() {
                return this.hasApplicationRole(ApplicationRole.DatabaseAdministrator, ApplicationRole.Business);
            }
        },
        methods: {
            /**
             * @description Reloads data
             */
            reload() {
                this.loadData();
            },

            /**
             * @description Loads the people in organization with a business business role
             */
            loadData() {
                const vm = this;

                OrganizationService.getOrganizationRoles(this.organizationId, false)
                    .then(response => {
                        vm.personOrganizationRoles = response;
                    }).catch(() => {
                    vm.redirectToError();
                })
            },

            /**
             * @description Opens up a modal to confirm deletion of the person organization role
             */
            onDeleteClick(deletingPersonOrganizationRole) {
                this.deletingPersonOrganizationRole = deletingPersonOrganizationRole;
            },

            /**
             * @description When the user clicks 'Confirm' - the selected organization business role will be deleted and the modal will be closed.
             */
            onDeleteConfirmed() {
                const vm = this;

                OrganizationService.deleteOrganizationRole(vm.deletingPersonOrganizationRole.id).then(() => {
                    vm.alert(ToastType.Success, `The ${vm.deletingPersonOrganizationRole.personName} was successfully deleted from the ${vm.deletingPersonOrganizationRole.organizationRoleName} business role.`);

                    vm.reload();
                }).catch(error => {
                        vm.alert(ToastType.Danger, error.response)
                    })
                    .finally(() => {
                        vm.deletingPersonOrganizationRole = null;
                    });
            },

            /**
             * @description When the user clicks the up arrow the person's order for the business role is updated
             */
            onMoveUpClick(personOrganizationRoleMoveUp) {
                const vm = this;

                OrganizationService.updateOrganizationRole({
                    id: personOrganizationRoleMoveUp.id,
                    isPrimaryContact: false,
                    organizationRoleOrdinal: personOrganizationRoleMoveUp.organizationRoleOrdinal - 1
                }).then(() => {
                    vm.alert(ToastType.Success, `The ${personOrganizationRoleMoveUp.personName} was successfully udpated for the ${personOrganizationRoleMoveUp.organizationRoleName} business role.`);

                    vm.reload();
                }).catch(error => {
                        vm.alert(ToastType.Danger, error.response)
                    });
            },

            /**
             * @description When the user clicks the down arrow the person's order for the business role is updated
             */
            onMoveDownClick(personOrganizationRoleMoveDown) {
                const vm = this;

                OrganizationService.updateOrganizationRole({
                    id: personOrganizationRoleMoveDown.id,
                    isPrimaryContact: false,
                    organizationRoleOrdinal: personOrganizationRoleMoveDown.organizationRoleOrdinal + 1
                }).then(() => {
                    vm.alert(ToastType.Success, `The ${personOrganizationRoleMoveDown.personName} was successfully udpated for the ${personOrganizationRoleMoveDown.organizationRoleName} business role.`);

                    vm.reload();
                }).catch(error => {
                        vm.alert(ToastType.Danger, error.response)
                    });
            },

            /**
             * @description Sets active section when tab changed
             */
            sectionChange (sectionIndex) {
                if (sectionIndex == this.activeRoleSection) {
                  this.activeRoleSection = null;
                } else {
                  this.activeRoleSection = sectionIndex;
                }
            }
        },
        created() {
            this.isLoaded = false;
            this.loadData();
            this.isLoaded = true;
        }
    }
</script>
